.chord {
  /* Out-of-key chord-name colour, scoped to the chip. The shared
   * --tuna-notes-outOfScale orange is about 3.8:1 on the light background, under
   * 4.5:1 for 15px/500 text, so the name mixes it toward the primary text
   * colour: darker on light backgrounds and lighter on dark ones. The dotted
   * underline keeps the shared token. */
  --tuna-music-outOfKey-text: color-mix(in oklch, var(--tuna-notes-outOfScale) 70%, var(--tuna-palette-text-primary));
  position: relative;
  display: inline-flex;
  padding: 1px var(--tuna-chord-chip-padding-inline);
  margin: 2px var(--tuna-chord-chip-margin-inline) 4px;
  border-radius: var(--tuna-shape-radius-sm);
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  vertical-align: baseline;
  line-height: 1.2;
}

.chord--inScale {
  color: var(--tuna-notes-inScale);
}

/* Theory cues off: chord names only, no marking of any kind. */
.chord--plain {
  color: var(--tuna-palette-text-primary);
  text-decoration: none;
}

.chord--outOfScale {
  color: var(--tuna-music-outOfKey-text);
  /* Non-chromatic indicator (WCAG 2.2 SC 1.4.1 — color must not be the sole means
   * of conveying information). The dotted underline accompanies the color change
   * so color-blind users still get a signal that the chord is outside the key.
   * `text-decoration-color` uses the same out-of-scale token, so the underline
   * picks up theme changes and reads as part of the chord's identity.
   */
  text-decoration: underline dotted;
  text-decoration-color: var(--tuna-notes-outOfScale);
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}

/* Match the editor-wide selection identity: same selection token used by native
 * ::selection and the block-level data-in-range bar. One color across text glyphs,
 * per-chord rectangles, and block bars so single-element and range selections read as
 * one consistent system. */
.chord--selected {
  background: var(--tuna-palette-selection);
}

.chord--clickable {
  text-decoration: underline;
}

/* S3a — modal characteristic-note glow (Joni UX spec §2.3).
 *
 * Two simultaneous treatments stacked via two pseudo-elements (Advanced
 * theory cues only):
 *   1. Persistent 2px right-edge accent bar (::after) — the non-chromatic
 *      indicator. A vertical bar (not just color) so color-blind users still
 *      get a signal. Uses `--tuna-palette-info-light` and sits inside the
 *      chip's rounded border so it reads as part of the chord identity.
 *   2. Enter-pulse overlay (::before) — one-shot 600ms fading background tint
 *      when the chord first mounts. The pulse is a positioned overlay (not a
 *      background on the chip itself), so it never touches the chip's
 *      selection background.
 *
 * `prefers-reduced-motion: reduce` suppresses the enter-pulse animation only;
 * the persistent bar remains so the non-chromatic indicator never disappears.
 */
.chord--modal {
  position: relative;
  overflow: hidden;
}

.chord--modal::before {
  content: '';
  position: absolute;
  inset: 0;
  background: color-mix(in oklch, var(--tuna-palette-info-light) 12%, transparent);
  border-radius: inherit;
  pointer-events: none;
  /* One-shot pulse: fade 12% tint → 0% over 600ms, then hold transparent. */
  animation: modal-pulse 600ms ease-out 1 forwards;
}

.chord--modal::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 2px;
  background: var(--tuna-palette-info-light);
  border-radius: 0 var(--tuna-shape-radius-sm) var(--tuna-shape-radius-sm) 0;
  /* Pointer-events disabled: the bar is a visual indicator only — clicks pass
   * through to the chord chip's onClick. */
  pointer-events: none;
}

@keyframes modal-pulse {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .chord--modal::before {
    /* Reduced motion: skip the fading overlay entirely. The persistent bar
     * (::after) is the non-chromatic indicator and remains visible. */
    animation: none;
    display: none;
  }
}

/* ── Tension Contour Stripe (Joni visual spec §2–§3, §9) ─────────────────────
 *
 * An under-stripe in the chip's bottom margin gutter, just BELOW the chip (spec §2),
 * not inside it: inside, it painted over the selection background, where every
 * bucket measured 1.0–1.7:1 in both themes and the stripe vanished on a selected
 * chip. Dual-encoded from one analyzer bucket: hue (--tuna-tension-*) here, and
 * HEIGHT (1px→5px) via an inline style on the element so TS owns a single height
 * source (no CSS/TS drift). The chip's `margin-bottom: 4px` reserves the gutter,
 * so painting the stripe causes zero layout shift (peak's 5px overhangs it by 1px).
 *
 * aria-hidden + pointer-events:none — the stripe is decorative-redundant; its
 * meaning is mirrored into the chip's aria-label (visual spec §8).
 */
.tensionStripe {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  /* height set inline (per-bucket, the SC 1.4.1 redundant channel) */
  /* A 3px radius exceeds the rest/low/medium stripe heights (1–3px) and clips
   * their left/right tips into rounded dots. Cap at 1px so the thin buckets
   * read as a clean full-width "ground line" (spec §2 metaphor), while the
   * taller high/peak stripes keep a barely-softened bottom corner. */
  border-radius: 0 0 1px 1px; /* tuna-allow: 1px cap, the stripe is 1-3px tall */
  pointer-events: none;
  /* prefers-reduced-motion handled below; default is an instant paint with an
   * opacity fade-in so the stripe doesn't pop as the analyzer resolves. */
  opacity: 1;
  animation: tension-fade-in 240ms ease-out 1;
}

.tensionStripe--rest {
  background: var(--tuna-tension-rest);
}
.tensionStripe--low {
  background: var(--tuna-tension-low);
}
.tensionStripe--medium {
  background: var(--tuna-tension-medium);
}
.tensionStripe--high {
  background: var(--tuna-tension-high);
}
.tensionStripe--peak {
  background: var(--tuna-tension-peak);
}

/* Muted / low-confidence (visual spec §9): TWO muted channels so "uncertain"
 * is itself not colour-only — desaturated (mixed toward muted text) AND a
 * dashed fill (a repeating horizontal dash via a gradient overlay). Applied
 * after a bucket class, so it overrides the solid background. */
.tensionStripe--muted {
  /* A pale medium hue under full-strength muted dashes. The earlier 60% dash over a
   * muted-brown mix read as a muddy texture at 3px, barely apart from a confident
   * medium; pale-vs-solid on a 4px/3px rhythm reads as dashed at 1x in both themes. */
  background: color-mix(in oklch, var(--tuna-tension-medium) 35%, transparent);
  background-image: repeating-linear-gradient(
    to right,
    var(--tuna-content-muted) 0,
    var(--tuna-content-muted) 4px,
    transparent 4px,
    transparent 7px
  );
}

@keyframes tension-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tensionStripe {
    /* Reduced motion: no fade — instant paint (mirror the modal-glow query). */
    animation: none;
  }
}

/*
 * Cover-song "check this chord" marker (docs/ux/2026-10-18-cover-song-chord-check-ux.md §1): a 6px hollow
 * ring at the chip's top-right, outside the name, so the name's width and legibility don't change. Shape
 * carries the signal, not colour; the secondary content token clears 3:1 for non-text. Decorative: the
 * chip's accessible name carries the meaning. Never printed: it's review state, not content.
 */
.checkRing {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 6px;
  height: 6px;
  box-sizing: border-box;
  border: 1.5px solid var(--tuna-content-secondary, var(--tuna-palette-text-secondary));
  border-radius: var(--tuna-shape-radius-circle);
  pointer-events: none;
}

@media print {
  .checkRing {
    display: none;
  }
}
